import React, { useState, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { space, breakpoints } from '@moda/tokens';
import ChevronLeftIcon from '@moda/icons/chevron-left-60';
import ChevronRightIcon from '@moda/icons/chevron-right-60';
import Swiper, { SwiperOptions } from 'swiper';
import { remToUnitlessPx } from '../../lib/remToUnitlessPx';
import { useSwiper } from '../Swiper';

import './SwiperCarousel.scss';

const DEFAULT_CONFIG: SwiperOptions = {
  speed: 500,
  spaceBetween: remToUnitlessPx(space.map['three-quarter-x']),
  slidesPerView: 2,
  slidesPerGroup: 2,
  breakpoints: {
    [remToUnitlessPx(breakpoints.xs)]: {
      slidesPerView: 3,
      slidesPerGroup: 3
    },
    [remToUnitlessPx(breakpoints.sm)]: {
      slidesPerView: 4,
      slidesPerGroup: 4
    },
    [remToUnitlessPx(breakpoints.md)]: {
      slidesPerView: 5,
      slidesPerGroup: 5
    },
    [remToUnitlessPx(breakpoints.lg)]: {
      slidesPerView: 6,
      slidesPerGroup: 6
    }
  }
};

const SMALL_SIZE = 6;
const CHEVRON_SIZE = space.scale[SMALL_SIZE];

interface Props {
  defaultConfig?: SwiperOptions;
  className?: string;
  classNameContainer?: string;
  classNameNavigation?: string;
}

export const SwiperCarousel: React.FC<Props> = ({
  defaultConfig = DEFAULT_CONFIG,
  className,
  classNameContainer,
  classNameNavigation,
  children
}) => {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const prevButtonRef = useRef<HTMLDivElement | null>(null);
  const nextButtonRef = useRef<HTMLDivElement | null>(null);
  const paginationElementRef = useRef<HTMLDivElement | null>(null);

  const config: SwiperOptions = useMemo(
    () => ({
      ...defaultConfig,
      preloadImages: true,
      centerInsufficientSlides: true,
      updateOnImagesReady: true,
      navigation: {
        prevEl: prevButtonRef.current || undefined,
        nextEl: nextButtonRef.current || undefined,
        disabledClass: 'SwiperCarousel__navigation--disabled'
      },
      pagination: paginationElementRef.current
        ? {
            el: paginationElementRef.current,
            type: 'bullets',
            clickable: true,
            hideOnClick: false,
            bulletClass: 'SwiperCarousel__bullet',
            bulletActiveClass: 'SwiperCarousel__bullet--active'
          }
        : undefined
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevButtonRef.current, nextButtonRef.current, paginationElementRef.current]
  );

  // Use Swiper's API to hacky-fix an annoying conflict with the <Image> component.
  const fixDuplicateSlides = function (this: Swiper) {
    this.$el
      .find('.swiper-slide-duplicate img')
      .removeClass('Image--pending')
      .addClass('Image--loaded');
  };
  const swiperRef = useSwiper(containerElement, config);
  swiperRef.current?.on('imagesReady', fixDuplicateSlides);

  return React.Children.count(children) ? (
    <div className={classNames('SwiperCarousel', className)}>
      <div className="SwiperCarousel__rail">
        <div
          className={classNames('SwiperCarousel__swiper-container', classNameContainer)}
          ref={(element: HTMLElement | null) => setContainerElement(element)}
        >
          <div className="swiper-wrapper SwiperCarousel__slides">
            {React.Children.map(children, (child, index) => (
              <div key={index} className="swiper-slide SwiperCarousel__slide">
                {child}
              </div>
            ))}
          </div>
        </div>

        <div
          className={classNames('SwiperCarousel__prev', classNameNavigation)}
          ref={prevButtonRef}
        >
          <ChevronLeftIcon width={CHEVRON_SIZE} height={CHEVRON_SIZE} />
        </div>
        <div
          className={classNames('SwiperCarousel__next', classNameNavigation)}
          ref={nextButtonRef}
        >
          <ChevronRightIcon width={CHEVRON_SIZE} height={CHEVRON_SIZE} />
        </div>
        <div className="swiper-pagination SwiperCarousel__pagination" ref={paginationElementRef} />
      </div>
    </div>
  ) : null;
};
